<template>
  <div class="pt-3 px-3">
    <h4 class="mb-3">Meu Plano</h4>

    <div v-if="state.loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <!-- Ja tem assinatura -->
    <div v-else-if="state.subscription && state.subscription.has_subscription">
      <div class="card mb-4">
        <div class="card-body">
          <div class="d-flex align-items-center mb-3">
            <div class="flex-grow-1">
              <h5 class="mb-1">{{ state.subscription.plan.name }}</h5>
              <div class="text-muted">{{ state.subscription.plan.label }}</div>
            </div>
            <span
              class="badge"
              :class="statusBadgeClass(state.subscription.status)"
            >
              {{ statusLabel(state.subscription.status) }}
            </span>
          </div>
          <div class="row g-3">
            <div class="col-md-4">
              <small class="text-muted d-block">Valor do plano</small>
              <strong>R$ {{ formatPrice(planTotal(state.subscription)) }}</strong>
            </div>
            <div class="col-md-4">
              <small class="text-muted d-block">Data do pagamento</small>
              <strong>{{ formatDate(state.subscription.last_payment_date) || '-' }}</strong>
            </div>
            <div class="col-md-4">
              <small class="text-muted d-block">Válido até</small>
              <strong>{{ formatDate(state.subscription.access_until || getValidUntil(state.subscription)) || '-' }}</strong>
            </div>
          </div>
          <div v-if="state.subscription.status === 'authorized'" class="mt-4">
            <button class="btn btn-outline-danger" @click="cancelar">
              Cancelar plano
            </button>
          </div>
          <div v-if="state.subscription.status === 'pending'" class="mt-4">
            <div class="alert alert-info mb-3">
              Você iniciou o pagamento do plano, mas ainda não concluiu no Mercado Pago.
            </div>
            <button class="btn btn-primary" @click="assinar(state.subscription.plan.slug)">
              Retomar pagamento
            </button>
          </div>
        </div>
      </div>

      <div v-if="state.subscription.status === 'cancelled' || state.subscription.status === 'paused'">
        <h5 class="mb-3">Escolha um novo plano</h5>
        <div class="row g-3">
          <div v-for="p in state.plans" :key="p.slug" class="col-md-4">
            <div class="card h-100">
              <div class="card-body text-center">
                <h5>{{ p.name }}</h5>
                <p class="text-muted">{{ p.label }}</p>
                <h3 class="mb-1">R$ {{ formatPrice(p.monthly_price) }}</h3>
                <small class="text-muted d-block mb-3">/mês</small>
                <button class="btn btn-primary w-100" @click="assinar(p.slug)">Contratar plano</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Nao tem assinatura ainda -->
    <div v-else>
      <div class="alert alert-warning mb-3">
        Você ainda não tem um plano ativo. Escolha um plano abaixo.
      </div>
      <div class="row g-3">
        <div v-for="p in state.plans" :key="p.slug" class="col-md-4">
          <div class="card h-100">
            <div class="card-body text-center">
              <h5>{{ p.name }}</h5>
              <p class="text-muted">{{ p.label }}</p>
              <h3 class="mb-1">R$ {{ formatPrice(p.monthly_price) }}</h3>
              <small class="text-muted d-block mb-3">/mês</small>
              <button class="btn btn-primary w-100" @click="assinar(p.slug)" :disabled="state.processing">
                {{ state.processing ? 'Aguarde...' : 'Contratar plano' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import subscriptionRepository from '@/services/api/subscriptionRepository'

export default {
  name: 'MinhaAssinatura',
  setup() {
    const swal = inject('$swal')
    const store = useStore()

    const state = reactive({
      loading: false,
      processing: false,
      subscription: null,
      plans: [],
    })

    async function carregar() {
      state.loading = true
      try {
        const [meRes, plansRes] = await Promise.all([
          subscriptionRepository.me(),
          subscriptionRepository.plans(),
        ])
        state.subscription = meRes.data?.data || meRes.data
        state.plans = plansRes.data?.data || []

        // Se voltou ativo, libera o gate
        if (state.subscription?.is_active) {
          store.commit('SET_REQUIRES_SUBSCRIPTION', false)
        }
      } catch (e) {
        swal({ icon: 'error', title: 'Erro', text: 'Falha ao carregar o plano.' })
      } finally {
        state.loading = false
      }
    }

    async function assinar(slug) {
      state.processing = true
      try {
        const res = await subscriptionRepository.checkout(slug)
        const url = res.data?.data?.checkout_url
        if (url) {
          window.location.href = url
        } else {
          swal({ icon: 'error', title: 'Erro', text: 'Nao foi possivel gerar o link de pagamento.' })
        }
      } catch (e) {
        swal({
          icon: 'error',
          title: 'Erro',
          text: e?.response?.data?.message || 'Falha ao iniciar contratação do plano.',
        })
      } finally {
        state.processing = false
      }
    }

    async function cancelar() {
      const res = await swal({
        icon: 'warning',
        title: 'Cancelar plano?',
        text: 'Você continuará com acesso até o final do período pago.',
        input: 'textarea',
        inputLabel: 'Motivo (opcional)',
        inputPlaceholder: 'Conta pra gente por que está cancelando',
        showCancelButton: true,
        confirmButtonText: 'Cancelar plano',
        cancelButtonText: 'Voltar',
      })
      if (!res.isConfirmed) return
      try {
        await subscriptionRepository.cancel(res.value || null)
        swal({ icon: 'success', title: 'Cancelada', timer: 1500, showConfirmButton: false })
        await carregar()
      } catch (e) {
        swal({ icon: 'error', title: 'Erro', text: 'Nao foi possivel cancelar.' })
      }
    }

    function formatPrice(v) {
      return Number(v || 0).toFixed(2).replace('.', ',')
    }
    function formatDate(v) {
      if (!v) return ''
      const [y, m, d] = String(v).split('T')[0].split(' ')[0].split('-')
      if (!y || !m || !d) return ''
      return `${d}/${m}/${y}`
    }
    function planTotal(sub) {
      const p = sub?.plan || {}
      if (p.total_price != null) return p.total_price
      return Number(p.monthly_price || 0) * Number(p.months_commitment || 1)
    }
    function statusLabel(s) {
      return { authorized: 'Ativo', pending: 'Aguardando pagamento', paused: 'Pagamento pendente', cancelled: 'Cancelado' }[s] || s
    }
    function statusBadgeClass(s) {
      return {
        authorized: 'bg-success',
        pending: 'bg-warning text-dark',
        paused: 'bg-secondary',
        cancelled: 'bg-danger',
      }[s] || 'bg-secondary'
    }

    function getValidUntil(subscription) {
      if (!subscription?.last_payment_date || !subscription?.plan?.months_commitment) return null
      return moment(subscription.last_payment_date)
        .add(Number(subscription.plan.months_commitment), 'months')
        .format('YYYY-MM-DD')
    }

    onMounted(carregar)

    return { state, assinar, cancelar, formatPrice, formatDate, planTotal, statusLabel, statusBadgeClass, getValidUntil }
  },
}
</script>
